// frontend/src/components/common/PlanLimitModal.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { LimitType, usePlanUsage } from '../../hooks/usePlanUsage';

interface PlanLimitModalProps {
  type: LimitType;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
}

const LIMIT_INFO: Record<LimitType, { title: string; desc: string; icon: string }> = {
  lesson: {
    title: 'Günlük Ders Limitine Ulaştın',
    desc: 'Bugünlük öğrenme maratonunda harika ilerledin! Sınırsız ders erişimi için planını yükseltebilirsin.',
    icon: '📚',
  },
  ai_message: {
    title: 'Günlük YZ Mentor Limitine Ulaştın',
    desc: 'MinikBot ile günlük sohbet kotan doldu. 7/24 sınırsız YZ mentörlük için Pro pakete geçebilirsin.',
    icon: '🤖',
  },
  code_evaluation: {
    title: 'Günlük Kod Değerlendirme Limitine Ulaştın',
    desc: 'Bugün için kod analizi ve yapay zeka jüri değerlendirme hakkın doldu. Sınırsız kod incelemesi için planını yükselt!',
    icon: '⚡',
  },
  game_play: {
    title: 'Günlük Oyun Oynama Limitine Ulaştın',
    desc: 'Bugünkü eğitsel kodlama oyunu oynama limitine ulaştın. Sınırsız oyun keyfi için premium üye ol!',
    icon: '🎮',
  },
  project: {
    title: 'Maksimum Proje Limitine Ulaştın',
    desc: 'Oluşturabileceğin kişiselleştirilmiş proje sayısına ulaştın. Sınırsız proje üretmek için paketini yükselt.',
    icon: '💻',
  },
  quiz_generation: {
    title: 'Günlük Quiz Üretme Limitine Ulaştın',
    desc: 'Bugünlük özel AI sınav üretme limitin doldu. Sınırsız sınavlar üretip kendini test etmek için planını yükselt!',
    icon: '📝',
  },
  quiz_solve: {
    title: 'Günlük Quiz Çözme Limitine Ulaştın',
    desc: 'Bugün için quiz çözme kotan doldu. Yarın tekrar deneyebilir veya hemen Pro pakete geçerek sınırsız çözebilirsin!',
    icon: '🎓',
  },
};

export function PlanLimitModal({ type, isOpen, onClose, title, description }: PlanLimitModalProps) {
  const { usage } = usePlanUsage();
  if (!isOpen) return null;

  const info = LIMIT_INFO[type] ?? {
    title: 'Plan Limitine Ulaştın',
    desc: 'Bu özelliği daha fazla kullanabilmek için paketini yükseltebilirsin.',
    icon: '🚫',
  };

  const modalTitle = title || info.title;
  const modalDesc = description || info.desc;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(16, 20, 42, 0.75)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 2000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#FFFFFF',
          border: '1.5px solid var(--paper-3, #E6E0D2)',
          borderRadius: 24,
          padding: '36px 32px',
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 24px 60px -12px rgba(16, 20, 42, 0.35)',
          position: 'relative',
          animation: 'modalPop 0.25s ease forwards',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'none',
            border: 'none',
            fontSize: 20,
            cursor: 'pointer',
            color: 'var(--ink-muted, #71717A)',
          }}
        >
          ✕
        </button>

        <div style={{ fontSize: 56, marginBottom: 12 }}>{info.icon}</div>
        <h2 style={{ fontFamily: 'var(--f-display)', fontSize: '1.5rem', color: '#EF4444', marginBottom: 12 }}>
          {modalTitle}
        </h2>
        <p style={{ color: 'var(--ink-2, #3F3F46)', fontSize: '0.92rem', lineHeight: 1.6, marginBottom: 20 }}>
          {modalDesc}
        </p>

        {usage && (
          <div
            style={{
              background: 'var(--bg-card, #F7F4EC)',
              border: '1px solid var(--paper-3, #E6E0D2)',
              borderRadius: 14,
              padding: '12px 16px',
              fontSize: '0.85rem',
              color: 'var(--ink, #18181B)',
              fontWeight: 600,
              marginBottom: 24,
            }}
          >
            Mevcut Paket: <strong>{usage.planName}</strong> • Limitler gece yarısı sıfırlanır 🔄
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Link
            to="/pricing"
            className="lesson-btn primary"
            onClick={onClose}
            style={{
              display: 'block',
              textAlign: 'center',
              textDecoration: 'none',
              padding: '12px 20px',
              fontSize: '0.95rem',
              fontWeight: 700,
            }}
          >
            💎 Sınırsız Pakete Geç
          </Link>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--ink-muted, #71717A)',
              fontSize: '0.85rem',
              cursor: 'pointer',
              fontWeight: 600,
              padding: '6px',
            }}
          >
            Şimdilik Devam Et
          </button>
        </div>
      </div>
    </div>
  );
}
